import { MarkAttendance } from './../../states/applications/applications.actions';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { IAdministrationResponseModel, IAttendance, IMMIInterviewees } from '@tsin-core/states/administration/administration.model';
import { GetApplicationsReq, MarkAttendanceReq, ProUserApplication } from '@tsin-core/states/applications/applications.model';
import { PreScreening } from '@tsin-core/states/pre-screening/pre-screening.model';
import { environment } from '@tsin-env/environment';
import { forkJoin, map, Observable, shareReplay, tap } from "rxjs";

export const PRO_ADMIN_UPLOAD_LANGUAGE = `${environment.apiUrl2}/application/phase2/upload-language-proficiency`;
const BASE_URL = `${environment.apiUrl2}`;
const PRO_2_URL = `${environment.apiUrl2}/application/phase2`;

const APPLICATION_URL = `${environment.apiUrl2}/applications`;
const SINGLE_APPLICATION = `${environment.apiUrl2}/application/phase2/{applicationStepKey}/{applicationID}`;
const GET_RESEND_REFEREE_EMAILS = `${environment.apiUrl2}/application/phase2/resend-emails/{applicationNo}`;
const ADMIN_DATA_URL = `${environment.apiUrl2}/applications/admin-data/{examId}`;
const ADD_NOTE_URL = `${environment.apiUrl2}/{step}/add-note`;
const RESEND_SINGLE_REFERENCE_EMAIL = `${environment.apiUrl2}/{step}/reminder-email/single`;
const RESEND_REFERENCE_EMAIL = `${environment.apiUrl2}/{step}/reminder-email/{id}`;

const ADMIN_GET_APPLICATION_BY_NO = `${environment.apiUrl2}/applications/applicationNo/{applicationNo}`;

@Injectable({
  providedIn: 'root'
})
export class ApplicationService {

  http: HttpClient = inject(HttpClient);

  getApplications(payload: GetApplicationsReq): Observable<{ data: any[] }> {
    return this.http.get<{ data: any[] }>(`${APPLICATION_URL}/admin/review/${payload.examId}`, {
      params: { status: payload.status, decisionMade: payload.decisionMade! },
    });
  }

  getMMIApplications(payload: GetApplicationsReq): Observable<{ data: any[] }> {
    return this.http.get<{ data: any[] }>(`${APPLICATION_URL}/all`, {
      params: { examId: payload.examId, status: payload.status, decisionMade: payload.decisionMade! },
    });
  }

  getMMIInterviewees(examId: string): Observable<IMMIInterviewees[]> {
    return this.http.get<IMMIInterviewees[]>(`${BASE_URL}/schedules/mmi-interviewee/${examId}`);
  }

  assignApplicationToMMICohort(payload: { cohortId: string, applicationIds: string[] }): Observable<any> {
    return this.http.put<any>(`${APPLICATION_URL}/assign-mmi-applications`, payload);
  }

  markAttendance(payload: MarkAttendanceReq): Observable<IAttendance> {
    return this.http.post<IAttendance>(`${BASE_URL}/schedules/attendance`, payload);
  }

  initializeApplication(
    examId: string,
    userId: string,
    preScreeningData: PreScreening,
    language: 'en' | 'fr' = 'en',
  ): Observable<ProUserApplication> {
    return this.http.post<{ data: ProUserApplication }>(`${BASE_URL}/applications/phase1`, {
      userId,
      examId,
      steps: [],
      eligibility: {},
      preScreening: preScreeningData,
      language,
    })
      .pipe(map(res => res.data));
  }


  getProApplications(cb?: () => void): Observable<ProUserApplication[]> {
    return this.http.get<{ data: ProUserApplication[] }>(`${BASE_URL}/applications/user`).pipe(
      map((response: any) => response.data),
      tap(() => cb?.()),
    );
  }

  savePhaseOneApplication(userId: string, applicationId: string, examId: string, _: any, preScreeningData: any, steps: any): Observable<ProUserApplication> {
    return this.http.post<{ data: ProUserApplication }>(`${BASE_URL}/applications/phase1`, {
      userId,
      applicationId,
      examId: examId,
      steps: steps,
      eligibility: _.eligibility,
      preScreening: preScreeningData
    }).pipe(map(res => res.data));
  }

  submitPhaseOneApplication(applicationId: string): Observable<{ data: ProUserApplication }> {
    return this.http.post<{ data: ProUserApplication }>(`${BASE_URL}/applications/submission`, { applicationId });
  }

  unSubmitPhaseOneApplication(applicationId: string): Observable<{ data: ProUserApplication }> {
    return this.http.post<{ data: ProUserApplication }>(`${BASE_URL}/applications/unsubmission`, { applicationId });
  }

  unSubmitPhaseTwoApplication(applicationId: string): Observable<{ data: ProUserApplication }> {
    return this.http.post<{ data: ProUserApplication }>(`${BASE_URL}/application/phase2/unsubmission`, { applicationId });
  }

  markInReviewApplication(applicationId: string): Observable<{ data: ProUserApplication }> {
    return this.http.post<{ data: ProUserApplication }>(`${BASE_URL}/applications/mark-in-review`, { applicationId });
  }

  uploadAvatar(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<{ data: string }>(`${BASE_URL}/application/avatar`, formData)
      .pipe(map(res => res.data));
  }

  uploadDocument(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<{ data: string }>(`${BASE_URL}/application/document`, formData)
      .pipe(map(res => res.data));
  }

  saveFlag(payload: { applicationNo: string, message: string }): Observable<{ data: any }> {
    return this.http
      .post<{ data: any }>(`${BASE_URL}/flag`, payload)
      .pipe(shareReplay(1));
  }

  getFlags(applicationNo: string): Observable<{ data: any[] }> {
    return this.http
      .get<{ data: any[] }>(`${BASE_URL}/flag/application/${applicationNo}`)
      .pipe(shareReplay(1));
  }

  saveAdminStep(step: string, appNo: string, data: any): Observable<any> {
    return this.http.put<ProUserApplication>(`${APPLICATION_URL}/admin/review/${step}/${appNo}`, {
      stepName: step,
      applicationNo: appNo,
      adminData: data,
    });
  }

  savePhase2AdminStep(applicationStepKey: string, applicationNo: string, data: any): Observable<any> {
    return this.http.post<ProUserApplication>(`${BASE_URL}/application/phase2/admin-review/${applicationStepKey}/${applicationNo}`, {
      adminData: data,
    });
  }

  setApplicationDecision(applicationNo: string, examId: string, decision: string, comment?: string, conditions?: string[], reasons?: string[]): Observable<ProUserApplication> {
    return this.http.put<ProUserApplication>(`${APPLICATION_URL}/set-application-decision`,
      {
        applicationNo,
        examId,
        decision,
        comment,
        conditions,
        reasons
      });
  }

  savePhaseTwoApplication(payload: {
    id: string,
    applicationNo: string,
    examId: string,
    data: any,
    step: string,
    completed: boolean
  }): Observable<ProUserApplication> {
    const { step, ...payloadBody } = payload;
    console.log('Saving Phase 2 Application Step:', `${PRO_2_URL}/${step}`);
    return this.http.post<{ data: ProUserApplication }>(`${PRO_2_URL}/${step}`, payloadBody).pipe(
      map(res => res.data)
    );
  }

  getSingleStepApplication(applicationNo: string, applicationStepKey: string): Observable<{ application: ProUserApplication }> {
    return this.http.get<{ application: ProUserApplication }>(`${PRO_2_URL}/${applicationStepKey}/${applicationNo}`);
  }

  saveSinglePhaseTwoStep(stepSingle: string, data: any): Observable<{ data: any, application: ProUserApplication }> {
    return this.http.post<{ data: any, application: ProUserApplication }>(`${BASE_URL}/${stepSingle}`, data);
  }

  updateSinglePhaseTwoStep(stepSingle: string, data: any): Observable<{ data: any, application: ProUserApplication }> {
    return this.http.put<{ data: any, application: ProUserApplication }>(`${BASE_URL}/${stepSingle}`, data);
  }

  deleteReferees(refereeIds: string[], refereeType: string) {
    const deleteRequests = refereeIds.map((id) =>
      this.http.delete<{ data: any }>(`${BASE_URL}/${refereeType}/single/${id}`)
    );
    return forkJoin(deleteRequests);
  }


  submitPro2Application(application: any, examId: string): Observable<ProUserApplication> {
    const id = application?.id;
    const applicationNo = application?.applicationNo;
    return this.http.post<ProUserApplication>(`${PRO_2_URL}/submit-application`, { id, examId, applicationNo });
  }

  public getPhase2ApplicationRecords(
    step: string,
    applicationNumber: string
  ): Observable<{ data: any }> {
    return this.http
      .get<{ data: any }>(
        SINGLE_APPLICATION.replace('{applicationStepKey}', step).replace(
          '{applicationID}',
          applicationNumber
        )
      )
      .pipe(shareReplay(1));
  }


  getAdminData(applicationNo: string): Observable<any> {
    return this.http.get<any>(`${APPLICATION_URL}/admin-data/${applicationNo}`);
  }

  getApplicationRecords(
    step: string,
    applicationNo: string
  ): Observable<{ data: { adminData: any; steps: any } }> {
    return this.http.get<{ data: { adminData: any; steps: any } }>(`${APPLICATION_URL}/admin/review/${step}/${applicationNo}`).pipe(shareReplay(1)).pipe(shareReplay(1));
  }



  getFlagDetails(applicationNo: string): Observable<{ data: any[] }> {
    return this.http
      .get<{ data: any[] }>(`${BASE_URL}/flag/application/${applicationNo}`)
      .pipe(shareReplay(1));
  }

  updateFlagResolution(applicationNo: string, flags: { id: string, isResolved: boolean }[]): Observable<{ data: any }> {
    return this.http
      .put<{ data: any }>(`${BASE_URL}/flag/application/${applicationNo}`, flags)
      .pipe(shareReplay(1));
  }

  getAllApplications(payload: { examId: string, status?: string, portalDrawId?: string, phase2PortalDrawId?: string, decisionMade?: string }): Observable<ProUserApplication[]> {
    return this.http.get<{ data: ProUserApplication[] }>(`${BASE_URL}/applications/all`, {
      params: payload,
    }).pipe(map(res => res.data));
  }

  getExportedApplications(examId: string): Observable<{ data: any }> {
    return this.http.get<{ data: any }>(`${BASE_URL}/applications/generate/${examId}/all`);
  }

  getAllApplicationSteps(applicationId: string): Observable<any[]> {
    return this.http.get<{ data: any[] }>(`${BASE_URL}/applications/single/${applicationId}`).pipe(
      map(res => res.data)
    );
  }

  getEligibilityDetails(applicationId: string): Observable<{ data: any }> {
    return this.http.get<{ data: any }>(`${BASE_URL}/applications/eligibility/${applicationId}`);
  }

  setNeedClinicalDirectorReview({ applicationId, examId, needClinicalDirectorReview }: { applicationId: string, examId: string, needClinicalDirectorReview: boolean }): Observable<any> {
    return this.http.post<any>(`${BASE_URL}/applications/set-clinical-director-review`,
      {
        applicationId,
        examId,
        needClinicalDirectorReview
      });
  }

  openPhase2Application(phase2PayloadData: any): Observable<any> {
    return this.http
      .post<{ data: any }>(`${BASE_URL}/application/phase2/start-application`, phase2PayloadData)
      .pipe(map((res) => res.data));
  }

  public resendAllReferees(applicationID: string) {
    return this.http
      .get<any[]>(GET_RESEND_REFEREE_EMAILS.replace('{applicationNo}', applicationID))
      .pipe(shareReplay(1));
  }

  public getPhase2AdminData(examId: string): Observable<any> {
    return this.http.get<any>(ADMIN_DATA_URL.replace('{examId}', examId));
  }

  public addReferenceNote(step: string, noteFormData: any) {
    return this.http
      .post<any>(ADD_NOTE_URL.replace('{step}', step), noteFormData)
      .pipe(shareReplay(1));
  }

  public resendReferenceEmail(payload: { step: string, applicationNo: string }) {
    const { step, applicationNo } = payload;
    return this.http
      .post<any>(RESEND_REFERENCE_EMAIL.replace('{step}', step).replace('{id}', applicationNo), {})
      .pipe(shareReplay(1));
  }

  public resendSingleReferenceEmail(payload: { step: string, email: string, applicationNo: string }) {
    return this.http
      .post<any>(RESEND_SINGLE_REFERENCE_EMAIL.replace('{step}', payload.step), {
        email: payload.email,
        applicationNo: payload.applicationNo
      })
      .pipe(shareReplay(1));
  }

  public resendSingleRotationEquivalences(payload: { applicationNo: string, refereeId: string }) {
    return this.http
      .post<any>(`${environment.apiUrl2}/rotation-equivalence/reminder-email/single`, {
        applicationNo: payload.applicationNo,
        refereeId: payload.refereeId
      })
      .pipe(shareReplay(1));
  }

  public getAdminApplicationByNo(applicationNo: string): Observable<any> {
    return this.http.get<any>(ADMIN_GET_APPLICATION_BY_NO.replace('{applicationNo}', applicationNo));
  }

  generateTestCandidates(phase: 'phase1' | 'phase2', examId?: string): Observable<{ data: any }> {
    const body: any = { phase };
    if (examId) {
      body.examId = examId;
    }
    return this.http.post<{ data: any }>(`${APPLICATION_URL}/test-candidates/generate`, body);
  }

  cleanupTestCandidates(): Observable<{ data: any }> {
    return this.http.delete<{ data: any }>(`${APPLICATION_URL}/test-candidates/cleanup`);
  }

}
